// deps
import React, { memo, ReactElement, useMemo, useCallback } from "react";

// helpers
import { getPaymentMethodCode } from "@payment-ui/helpers/Wallet";
import {
    getPaymentMethodIconUrl,
    getPaymentMethodDiscount,
    getPaymentMethodDisplayName,
} from "@payment-ui/helpers/Payment";
import { CheckoutRoute, useNavigate } from "@swiggy-private/checkout-core";

// interfaces
import { IWalletPaymentGroup, WalletPaymentMethodData } from "@payment-ui/interfaces/Wallet";
import { PaymentMethodProps } from "@payment-ui/interfaces/Payment";
import { User } from "@swiggy-private/payment-core";

// components
import PaymentMethodIcon from "@payment-ui/widgets/payment-method-icon/PaymentMethodIcon";
import OtherPaymentOffersGenericUI from "../../OtherPaymentOffersGenericUI";

// payment-testids
import { getWalletTestIds } from "@swiggy-private/payment-test-ids";

interface WalletProps extends PaymentMethodProps {
    methodData: WalletPaymentMethodData;
    paymentActions?: IWalletPaymentGroup["paymentActions"];
    user?: User;
    paymentAmount?: string;
    paymentGroup?: IWalletPaymentGroup["paymentData"];
}

const WALLET_PAYMENT_ICON_WIDTH = 32;

function WalletPaymentMethod({ user, paymentGroup, ...props }: WalletProps): ReactElement | null {
    const navigateTo = useNavigate();

    const paymentCode = useMemo(() => getPaymentMethodCode(props.methodData), [props.methodData]);
    const walletTestIds = useMemo(() => getWalletTestIds(paymentCode), [paymentCode]);

    const onClick = useCallback(
        (event: React.MouseEvent): void => {
            event.preventDefault();
            navigateTo(CheckoutRoute.MORE_PAYMENT_METHODS, { paymentGroup });
            return;
        },
        [navigateTo, paymentGroup],
    );

    const paymentMethodIcon = useMemo(
        (): ReactElement => (
            <PaymentMethodIcon
                src={getPaymentMethodIconUrl(props.methodData)}
                alt={props.methodData.displayName}
                width={WALLET_PAYMENT_ICON_WIDTH}
                data-testid={walletTestIds.ICON}
            />
        ),
        [props.methodData, walletTestIds],
    );

    const displayName = getPaymentMethodDisplayName(props.methodData);
    const subtext = getPaymentMethodDiscount(props.methodData);

    return (
        <OtherPaymentOffersGenericUI
            onClick={onClick}
            displayName={displayName}
            subtext={subtext}
            paymentMethodIcon={paymentMethodIcon}
        />
    );
}

export default memo(WalletPaymentMethod);
